import { useQueryClient } from '@tanstack/react-query';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import i18next from 'i18next';
import { Comment } from 'infrastructure/classes/comments/comment';
import { API_PATIENTS_COMMENTS } from 'services/dataService/resources';
import { generalCommentWithUndo } from 'infrastructure/functions/general-comment-with-undo';

import useApi from '../use-api';
import { generateCommentsQueryKey } from './common';

import type { IPaginatedCommentsData } from './types';
import type { InfiniteData } from '@tanstack/react-query';
import type { ICreateComment } from 'infrastructure/interfaces';

interface ISendCommentOptions {
  withTimeout?: boolean;
  showResult?: boolean;
}

export const useSendComment = () => {
  const queryClient = useQueryClient();
  const { loading, createData } = useApi({ canAbort: false });

  const setQuery = (queryKey: unknown[], c: Comment, removeMock = false) => {
    queryClient.setQueryData<InfiniteData<IPaginatedCommentsData>>(
      queryKey,
      (od) => {
        if (!od) {
          return {
            pages: [{ data: [c], count: 1 }],
            pageParams: [undefined],
          };
        }
        const oldData = structuredClone(od);

        const updatedPages = [...oldData.pages];
        const firstPage = updatedPages[0];
        const newData = removeMock
          ? [c, ...firstPage.data.filter((el) => el.createdBy !== 'mock')]
          : [c, ...firstPage.data];
        updatedPages[0] = {
          ...firstPage,
          data: newData,
        };

        return {
          ...oldData,
          pages: updatedPages,
        };
      },
    );
  };

  const sendComment = async (
    body: ICreateComment,
    options?: ISendCommentOptions,
  ): Promise<boolean> => {
    const queryKey = generateCommentsQueryKey(body);
    const { withTimeout = true, showResult = true } = options ?? {};

    const { mockComment, undoTimeoutPromise } = generalCommentWithUndo(
      body,
      withTimeout,
    );

    setQuery(queryKey, mockComment);

    try {
      await undoTimeoutPromise;

      const res = await createData(API_PATIENTS_COMMENTS, body);
      const comment = new Comment(res);

      setQuery(queryKey, comment, true);
      if (showResult) {
        notificationController.success({
          message: i18next.t('labels.commentSubmitted'),
        });
      }

      return true;
    } catch (error) {
      queryClient.setQueryData<Comment[]>(
        queryKey,
        (oldData) => oldData?.filter((c) => c.guid !== 'mock') || [],
      );
      return false;
    }
  };

  return { loading, sendComment };
};
